import React from "react";
import Button from "components/button";
import Tooltip from "components/tooltips/Tooltips";
import { Euro, InformationIcon, Localisation, Pencil, TrashIcon } from "assets/icons";

const formatWage = (wage) => {
    if (typeof wage !== "number") return "—";
    return new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(wage);
};

const formatAddressLine = (location) => {
    if (!location) return "";
    if (typeof location === "string") return location.trim();
    const { street, postcode, city, department } = location;
    return [street, postcode, city, department].filter(Boolean).join(" ");
};

const actionBtnVisibility =
    "sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto sm:group-focus-within:opacity-100 sm:group-focus-within:pointer-events-auto transition-opacity duration-150";

const TemplateCard = ({
    template,
    selectable = false,
    selected = false,
    onSelect,
    onEdit,
    onDelete,
    onPreview,
}) => {
    if (!template) return null;
    const isBlocked = template.isBelowMinimumWage;
    const wage = template.salary?.wage_per_hour;
    const title = template.name || template.title || "Sans nom";
    const addressLine = formatAddressLine(template.location);

    const handleSelect = () => {
        if (!selectable || isBlocked) return;
        onSelect?.(template);
    };

    const handleCardClick = () => {
        if (selectable) {
            handleSelect();
            return;
        }
        onPreview?.(template);
    };

    const showActions = onEdit || onDelete;

    return (
        <div
            className={`
                group relative rounded-lg border bg-white px-4 py-3.5 transition-colors
                ${
                    isBlocked
                        ? "border-red-100 bg-red-50/40"
                        : selected
                        ? "border-amber-300 bg-amber-50/40"
                        : "border-gray-200 hover:border-gray-300"
                }
                ${
                    selectable && !isBlocked
                        ? "cursor-pointer"
                        : !selectable && onPreview
                        ? "cursor-pointer"
                        : ""
                }
            `}
            onClick={handleCardClick}
        >
            {showActions && (
                <div
                    className="absolute top-3 right-3 z-10 flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                >
                    {onEdit && (
                        <Tooltip text={"Modifier"} placement="top">
                            <Button
                                color={"SECONDARY"}
                                size={"ICON"}
                                className={`${actionBtnVisibility} !border-gray-200 shadow-sm`}
                                onClick={() => onEdit(template)}
                            >
                                <Pencil wh={16} color="#4B5563" />
                            </Button>
                        </Tooltip>
                    )}
                    {onDelete && (
                        <Tooltip text={"Supprimer"} placement="top">
                            <Button
                                color={"SECONDARY"}
                                size={"ICON"}
                                className={`${actionBtnVisibility} !border-gray-200 shadow-sm hover:!border-red-200 hover:!bg-red-50`}
                                onClick={() => onDelete(template)}
                            >
                                <TrashIcon wh={16} color="#B91C1C" />
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}

            <div
                className={`min-w-0 ${showActions ? "pr-1 min-[520px]:pr-[5.5rem]" : ""}`}
            >
                <h3 className="ft-base font-medium text-gray-900 leading-snug truncate">
                    {title}
                </h3>

                <div className="mt-3 flex flex-col gap-2.5">
                    <div className="flex items-start gap-2 min-w-0">
                        <span className="shrink-0 mt-0.5 text-gray-400">
                            <Localisation wh={16} color="#9CA3AF" />
                        </span>
                        <p className="ft-sm text-gray-600 leading-snug min-w-0 break-words">
                            {addressLine || "—"}
                        </p>
                    </div>
                    <div className="flex items-start gap-2 min-w-0">
                        <span className="shrink-0 mt-0.5 text-gray-400">
                            <Euro wh={16} color="#9CA3AF" />
                        </span>
                        <p className="ft-sm text-gray-600 leading-snug min-w-0 break-words">
                            {typeof wage === "number" && !Number.isNaN(wage)
                                ? `${formatWage(wage)} € par heure`
                                : "—"}
                        </p>
                    </div>
                </div>
            </div>

            {isBlocked && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200/80 bg-white/80 px-2.5 py-2">
                    <div className="shrink-0 mt-0.5">
                        <InformationIcon wh={14} color="#B91C1C" />
                    </div>
                    <p className="ft-xs text-red-800 leading-relaxed">
                        Sous le SMIC — mettez la fiche à jour avant de créer une
                        mission.
                    </p>
                </div>
            )}
        </div>
    );
};

export default TemplateCard;
